/*
 * Kinsora PDP Image Lightbox — custom, zero-dependency
 * ----------------------------------------------------
 * Click/tap the main product image → full-screen lightbox with:
 *   • Tap/click to zoom 2x at that point, tap again to zoom out
 *   • Swipe left/right on mobile to change images
 *   • Swipe down on mobile to close
 *   • Arrow buttons + keyboard arrows on desktop
 *   • Bottom bar (counter, zoom, close) sits directly under the image —
 *     no absolute-positioning hacks, it just flows beneath
 *
 * Data source: the hidden .kinsora-pswp-gallery anchors rendered by the
 * product section (href + data-pswp-srcset + data-global-index + <img>).
 * We keep that DOM as-is so no Liquid templates need to change.
 */
(function () {
    'use strict';

    var MIN_SCALE = 1;
    var MAX_SCALE = 4;
    var DOUBLE_TAP_SCALE = 2.5;

    var STATE = {
        overlay: null,
        images: [],       // [{fullSrc, srcset, alt}]
        index: 0,
        // Transform state — single source of truth for image position/zoom
        scale: 1,
        tx: 0,
        ty: 0,
        pinch: null,      // active two-finger pinch
        touch: null,      // active one-finger gesture
        bodyOverflow: ''
    };

    /* ---------- inline CSS (injected once) ---------- */
    function injectStyles() {
        if (document.getElementById('kinsora-lightbox-styles')) return;
        var css =
            /* Keep Liquid anchor list hidden (data source only) */
            '.kinsora-pswp-gallery{display:none!important}' +
            /* PDP main image zoom cursor */
            '.main-image.kinsora-zoomable{cursor:zoom-in;position:relative}' +
            '.main-image.kinsora-zoomable img{cursor:zoom-in}' +
            '@media (hover:none){.main-image.kinsora-zoomable,.main-image.kinsora-zoomable img{cursor:default}}' +
            /* Tap-to-zoom hint pill — side configurable via data-zoom-hint-side */
            '.kinsora-zoom-hint{position:absolute;bottom:14px;right:14px;left:auto;display:inline-flex;align-items:center;gap:6px;padding:8px 12px;background:rgba(17,17,17,.78);color:#fff;font-size:12px;letter-spacing:.02em;border-radius:999px;pointer-events:none;opacity:0;transition:opacity .3s ease;z-index:3;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}' +
            '.kinsora-zoom-hint--left{left:14px;right:auto}' +
            '.kinsora-zoom-hint.is-visible{opacity:1}' +
            '.kinsora-zoom-hint svg{width:14px;height:14px;flex:0 0 14px}' +
            /* Lightbox overlay — vertically-centered column so controls sit flush under the image */
            '.klb-overlay{position:fixed;inset:0;z-index:99999;background:rgba(10,10,10,.97);display:flex;flex-direction:column;align-items:stretch;justify-content:center;opacity:0;transition:opacity 200ms ease;-webkit-tap-highlight-color:transparent;overscroll-behavior:contain;touch-action:none}' +
            '.klb-overlay *{touch-action:none}' +
            '.klb-overlay.is-open{opacity:1}' +
            '.klb-stage{position:relative;flex:0 0 auto;display:flex;align-items:center;justify-content:center;width:100%}' +
            '.klb-track{position:relative;display:flex;align-items:center;justify-content:center;transition:transform 280ms cubic-bezier(.22,.61,.36,1);max-width:100%}' +
            '.klb-track.is-dragging{transition:none}' +
            /* Image: explicit width/height set by JS (fitImageAndStage) so every image fills the available space regardless of source resolution */
            '.klb-img{display:block;transform-origin:50% 50%;transition:transform 280ms cubic-bezier(.22,.61,.36,1);will-change:transform;user-select:none;-webkit-user-drag:none;cursor:zoom-in}' +
            '.klb-img.is-zoomed{cursor:zoom-out}' +
            /* Side arrows (desktop) */
            '.klb-arrow{position:absolute;top:50%;transform:translateY(-50%);width:46px;height:46px;border-radius:50%;background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.18);color:#fff;font-size:22px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:2;padding:0;transition:background 150ms ease}' +
            '.klb-arrow:hover{background:rgba(0,0,0,.8)}' +
            '.klb-arrow-prev{left:16px}' +
            '.klb-arrow-next{right:16px}' +
            '.klb-arrow[disabled]{opacity:.4;cursor:default}' +
            /* Controls bar (counter, zoom, close) — flows directly under stage */
            '.klb-controls{flex:0 0 auto;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 22px;padding-bottom:calc(14px + env(safe-area-inset-bottom,0px));background:rgba(0,0,0,.6);color:#fff;font-family:"Newsreader",Georgia,"Times New Roman",serif}' +
            '.klb-counter{font-size:13px;letter-spacing:.04em;opacity:.9;min-width:52px}' +
            '.klb-actions{display:flex;align-items:center;gap:10px}' +
            '.klb-btn{appearance:none;background:transparent;border:1px solid rgba(255,255,255,.28);color:#fff;width:40px;height:40px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;transition:background 150ms ease,border-color 150ms ease}' +
            '.klb-btn:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.5)}' +
            '.klb-btn svg{width:18px;height:18px;stroke:#fff;stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round}' +
            /* Hide arrows on mobile (swipe instead) */
            '@media (max-width:767px){.klb-arrow{display:none}.klb-controls{padding:12px 18px;padding-bottom:calc(12px + env(safe-area-inset-bottom,0px))}.klb-counter{font-size:12px}}' +
            /* Prevent body scroll while open */
            'body.klb-locked{overflow:hidden!important;touch-action:none}';
        var style = document.createElement('style');
        style.id = 'kinsora-lightbox-styles';
        style.appendChild(document.createTextNode(css));
        document.head.appendChild(style);
    }

    /* ---------- collect images from hidden gallery anchors ---------- */
    function collectImages() {
        var anchors = document.querySelectorAll('.kinsora-pswp-gallery a');
        var list = [];
        for (var i = 0; i < anchors.length; i++) {
            var a = anchors[i];
            var thumb = a.querySelector('img');
            list.push({
                fullSrc: a.getAttribute('href') || '',
                srcset: a.getAttribute('data-pswp-srcset') || '',
                globalIndex: parseInt(a.getAttribute('data-global-index'), 10),
                alt: thumb ? (thumb.getAttribute('alt') || '') : ''
            });
        }
        return list;
    }

    function currentGlobalIdx() {
        if (window.Kinsora && typeof window.Kinsora.currentImageIndex === 'number') {
            return window.Kinsora.currentImageIndex;
        }
        if (typeof window.currentImageIndex === 'number') return window.currentImageIndex;
        return 0;
    }

    function indexOfGlobal(globalIdx) {
        for (var i = 0; i < STATE.images.length; i++) {
            if (STATE.images[i].globalIndex === globalIdx) return i;
        }
        return 0;
    }

    /* ---------- build overlay ---------- */
    function buildOverlay() {
        var o = document.createElement('div');
        o.className = 'klb-overlay';
        o.setAttribute('role', 'dialog');
        o.setAttribute('aria-modal', 'true');
        o.setAttribute('aria-label', 'Enlarged product image');
        o.innerHTML =
            '<div class="klb-stage">' +
                '<div class="klb-track">' +
                    '<img class="klb-img" alt="" draggable="false">' +
                '</div>' +
                '<button type="button" class="klb-arrow klb-arrow-prev" aria-label="Previous image">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
                '</button>' +
                '<button type="button" class="klb-arrow klb-arrow-next" aria-label="Next image">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
                '</button>' +
            '</div>' +
            '<div class="klb-controls">' +
                '<span class="klb-counter" aria-live="polite">1 / 1</span>' +
                '<div class="klb-actions">' +
                    '<button type="button" class="klb-btn klb-zoom" aria-label="Toggle zoom">' +
                        '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>' +
                    '</button>' +
                    '<button type="button" class="klb-btn klb-close" aria-label="Close">' +
                        '<svg viewBox="0 0 24 24"><line x1="6" y1="6" x2="18" y2="18"></line><line x1="6" y1="18" x2="18" y2="6"></line></svg>' +
                    '</button>' +
                '</div>' +
            '</div>';
        return o;
    }

    /* ---------- size image + stage to fill available space ----------
     * With `width:auto; height:auto` CSS, a low-resolution source
     * (e.g. an 800×800 product crop) renders at its intrinsic size
     * and looks tiny in the lightbox while a 2400×2400 shot fills
     * the viewport. We fix that here: every image is sized to the
     * largest box that fits within (viewport × viewport-minus-controls)
     * while preserving aspect. Stage height tracks the image, so the
     * controls bar always sits flush beneath the photo.
     */
    function fitImageAndStage() {
        if (!STATE.overlay) return;
        var stage = STATE.overlay.querySelector('.klb-stage');
        var img = STATE.overlay.querySelector('.klb-img');
        var controls = STATE.overlay.querySelector('.klb-controls');
        if (!img || !stage) return;
        if (!img.naturalWidth || !img.naturalHeight) return; // wait for load

        var natAspect = img.naturalWidth / img.naturalHeight;
        var viewW = window.innerWidth;
        var viewH = window.innerHeight;
        var controlsH = controls ? controls.offsetHeight : 72;
        var maxH = Math.max(160, viewH - controlsH - 8); // 8px breathing room
        var maxW = viewW;

        var displayW, displayH;
        if (maxH * natAspect <= maxW) {
            // Height-bound: image is taller than the max box's aspect
            displayH = maxH;
            displayW = maxH * natAspect;
        } else {
            // Width-bound: image is wider than the max box's aspect
            displayW = maxW;
            displayH = maxW / natAspect;
        }

        img.style.width = displayW + 'px';
        img.style.height = displayH + 'px';
        stage.style.height = displayH + 'px';
    }

    /* ---------- render current slide ---------- */
    function renderSlide() {
        if (!STATE.overlay) return;
        var data = STATE.images[STATE.index];
        if (!data) return;
        var img = STATE.overlay.querySelector('.klb-img');
        var counter = STATE.overlay.querySelector('.klb-counter');

        // Reset transform so the new slide starts at scale 1, centered
        resetTransform(false);

        // Size handler — runs once the new source has decoded
        img.onload = fitImageAndStage;

        if (data.srcset) img.setAttribute('srcset', data.srcset);
        else img.removeAttribute('srcset');
        img.setAttribute('sizes', '100vw');
        img.src = data.fullSrc;
        img.alt = data.alt;

        // If the browser served from cache, naturalWidth is already set → fit immediately
        if (img.complete && img.naturalWidth) fitImageAndStage();

        counter.textContent = (STATE.index + 1) + ' / ' + STATE.images.length;

        var prev = STATE.overlay.querySelector('.klb-arrow-prev');
        var next = STATE.overlay.querySelector('.klb-arrow-next');
        if (STATE.images.length <= 1) {
            prev.setAttribute('disabled', 'disabled');
            next.setAttribute('disabled', 'disabled');
        } else {
            prev.removeAttribute('disabled');
            next.removeAttribute('disabled');
        }
    }

    /* ---------- navigation ---------- */
    function go(dir) {
        if (STATE.images.length <= 1) return;
        STATE.index = (STATE.index + dir + STATE.images.length) % STATE.images.length;
        renderSlide();
    }
    function goPrev() { go(-1); }
    function goNext() { go(1); }

    /* ---------- transform helpers ----------
     * Single source of truth: STATE.scale, STATE.tx, STATE.ty.
     * All zoom / pinch / pan gestures mutate that trio and call applyTransform().
     * Using translate+scale with transform-origin 0 0 (top-left) keeps the math
     * uniform across tap-to-zoom, pinch-to-zoom, and one-finger panning.
     */
    function applyTransform(animate) {
        var img = STATE.overlay && STATE.overlay.querySelector('.klb-img');
        if (!img) return;
        img.style.transformOrigin = '0 0';
        img.style.transition = animate ? '' : 'none';
        img.style.transform = 'translate(' + STATE.tx + 'px,' + STATE.ty + 'px) scale(' + STATE.scale + ')';
        if (STATE.scale > 1.01) img.classList.add('is-zoomed');
        else img.classList.remove('is-zoomed');
    }
    function resetTransform(animate) {
        STATE.scale = 1;
        STATE.tx = 0;
        STATE.ty = 0;
        applyTransform(animate !== false);
    }
    function getImageBase() {
        // Pre-transform rect of the image. Because transform-origin is 0 0 and
        // we translate+scale, rect.left = baseLeft + tx, rect.width = baseW * scale.
        var img = STATE.overlay && STATE.overlay.querySelector('.klb-img');
        if (!img) return null;
        var r = img.getBoundingClientRect();
        var s = STATE.scale || 1;
        return {
            left: r.left - STATE.tx,
            top: r.top - STATE.ty,
            width: r.width / s,
            height: r.height / s
        };
    }
    function clampPan() {
        var b = getImageBase();
        if (!b) return;
        var viewW = window.innerWidth;
        var viewH = window.innerHeight;
        var controlsH = (STATE.overlay.querySelector('.klb-controls').offsetHeight) || 72;
        var visH = Math.max(100, viewH - controlsH); // visible area above controls
        var w = b.width * STATE.scale;
        var h = b.height * STATE.scale;

        if (w <= viewW) {
            STATE.tx = (viewW - w) / 2 - b.left;
        } else {
            var maxTx = -b.left;
            var minTx = viewW - b.left - w;
            STATE.tx = Math.max(minTx, Math.min(maxTx, STATE.tx));
        }
        if (h <= visH) {
            STATE.ty = (visH - h) / 2 - b.top;
        } else {
            var maxTy = -b.top;
            var minTy = visH - b.top - h;
            STATE.ty = Math.max(minTy, Math.min(maxTy, STATE.ty));
        }
    }

    /* ---------- tap / click zoom toggle ---------- */
    function toggleZoomAt(clientX, clientY) {
        if (STATE.scale > 1.01) {
            resetTransform(true);
            return;
        }
        var b = getImageBase();
        if (!b) return;
        var s = DOUBLE_TAP_SCALE;
        var px = typeof clientX === 'number' ? clientX : (b.left + b.width / 2);
        var py = typeof clientY === 'number' ? clientY : (b.top + b.height / 2);
        // Pivot in image-local coords (pre-transform, because scale=1, tx=ty=0 right now)
        var imgX = px - b.left;
        var imgY = py - b.top;
        STATE.scale = s;
        // tx/ty chosen so that image point (imgX, imgY) lands back on screen (px, py)
        STATE.tx = px - b.left - imgX * s;
        STATE.ty = py - b.top - imgY * s;
        clampPan();
        applyTransform(true);
    }

    /* ---------- multi-touch gesture handling ---------- */
    function _dist(t0, t1) {
        var dx = t1.clientX - t0.clientX;
        var dy = t1.clientY - t0.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    function _mid(t0, t1) {
        return { x: (t0.clientX + t1.clientX) / 2, y: (t0.clientY + t1.clientY) / 2 };
    }

    function onTouchStart(e) {
        if (e.touches.length >= 2) {
            // Begin pinch — wins over any active single-finger gesture
            STATE.touch = null;
            var track = STATE.overlay.querySelector('.klb-track');
            track.classList.remove('is-dragging');
            track.style.transform = '';

            var t0 = e.touches[0], t1 = e.touches[1];
            var mid = _mid(t0, t1);
            var b = getImageBase();
            if (!b) return;
            var imgX = (mid.x - b.left - STATE.tx) / STATE.scale;
            var imgY = (mid.y - b.top - STATE.ty) / STATE.scale;
            STATE.pinch = {
                startDist: _dist(t0, t1),
                startScale: STATE.scale,
                imgX: imgX,
                imgY: imgY,
                base: b
            };
            return;
        }
        if (e.touches.length === 1 && !STATE.pinch) {
            var t = e.touches[0];
            STATE.touch = {
                x: t.clientX, y: t.clientY, t: Date.now(),
                dx: 0, dy: 0, moved: false, dragging: false,
                startTx: STATE.tx, startTy: STATE.ty
            };
        }
    }

    function onTouchMove(e) {
        // Pinch takes precedence whenever 2+ fingers are down
        if (STATE.pinch && e.touches.length >= 2) {
            var t0 = e.touches[0], t1 = e.touches[1];
            var newMid = _mid(t0, t1);
            var newDist = _dist(t0, t1);
            var ratio = newDist / STATE.pinch.startDist;
            var newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, STATE.pinch.startScale * ratio));
            var b = STATE.pinch.base;
            STATE.scale = newScale;
            STATE.tx = newMid.x - b.left - STATE.pinch.imgX * newScale;
            STATE.ty = newMid.y - b.top - STATE.pinch.imgY * newScale;
            applyTransform(false);
            return;
        }
        if (!STATE.touch || STATE.pinch) return;
        if (e.touches.length !== 1) return;
        var t = e.touches[0];
        var dx = t.clientX - STATE.touch.x;
        var dy = t.clientY - STATE.touch.y;
        STATE.touch.dx = dx;
        STATE.touch.dy = dy;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) STATE.touch.moved = true;

        if (STATE.scale > 1.01) {
            // Pan the zoomed image — move with finger, live (no transition)
            STATE.tx = STATE.touch.startTx + dx;
            STATE.ty = STATE.touch.startTy + dy;
            applyTransform(false);
            return;
        }

        // Not zoomed: horizontal drag previews next/prev slide
        var track = STATE.overlay.querySelector('.klb-track');
        if (!STATE.touch.dragging && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
            STATE.touch.dragging = true;
            track.classList.add('is-dragging');
        }
        if (STATE.touch.dragging) {
            track.style.transform = 'translateX(' + dx + 'px)';
        }
    }

    function onTouchEnd(e) {
        // Ending pinch: when we drop below 2 touches, settle the transform
        if (STATE.pinch && (!e.touches || e.touches.length < 2)) {
            STATE.pinch = null;
            if (STATE.scale <= 1.02) {
                resetTransform(true);
            } else {
                clampPan();
                applyTransform(true);
            }
            // If one finger is still down, start a pan session from here
            if (e.touches && e.touches.length === 1) {
                var ft = e.touches[0];
                STATE.touch = {
                    x: ft.clientX, y: ft.clientY, t: Date.now(),
                    dx: 0, dy: 0, moved: true, dragging: false,
                    startTx: STATE.tx, startTy: STATE.ty
                };
            }
            return;
        }
        if (!STATE.touch) return;
        var touch = STATE.touch;
        STATE.touch = null;
        var track = STATE.overlay.querySelector('.klb-track');
        track.classList.remove('is-dragging');
        track.style.transform = '';

        var absX = Math.abs(touch.dx);
        var absY = Math.abs(touch.dy);
        var dt = Date.now() - touch.t;

        // Quick tap with no real movement → toggle zoom at the tap point
        if (!touch.moved && dt < 350) {
            var target = e.target;
            if (target && target.closest && (target.closest('.klb-btn') || target.closest('.klb-arrow') || target.closest('.klb-controls'))) {
                return;
            }
            // Edge-tap navigation (mobile has no visible arrows): when not zoomed and
            // there are multiple images, left 30% → prev, right 30% → next, center → zoom.
            if (STATE.scale <= 1.01 && STATE.images.length > 1) {
                var _stage = STATE.overlay.querySelector('.klb-stage');
                var _sr = _stage ? _stage.getBoundingClientRect() : null;
                if (_sr && _sr.width) {
                    var _frac = (touch.x - _sr.left) / _sr.width;
                    if (_frac <= 0.30) { goPrev(); return; }
                    if (_frac >= 0.70) { goNext(); return; }
                }
            }
            toggleZoomAt(touch.x, touch.y);
            return;
        }

        // Finished a pan while zoomed → clamp back into sensible bounds
        if (STATE.scale > 1.01) {
            clampPan();
            applyTransform(true);
            return;
        }

        // Not zoomed: horizontal swipe navigates, downward swipe closes
        if (absX > 60 && absX > absY) {
            if (touch.dx < 0) goNext();
            else goPrev();
            return;
        }
        if (touch.dy > 110 && absY > absX) {
            close();
        }
    }

    /* ---------- events ---------- */
    function onKey(e) {
        if (!STATE.overlay) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') goPrev();
        else if (e.key === 'ArrowRight') goNext();
    }

    function wireEvents() {
        var o = STATE.overlay;
        o.querySelector('.klb-close').addEventListener('click', close);
        o.querySelector('.klb-zoom').addEventListener('click', function () { toggleZoomAt(); });
        o.querySelector('.klb-arrow-prev').addEventListener('click', goPrev);
        o.querySelector('.klb-arrow-next').addEventListener('click', goNext);

        var img = o.querySelector('.klb-img');
        img.addEventListener('click', function (e) {
            // Desktop click-to-zoom-at-cursor (mobile uses touchend tap path)
            if ('ontouchstart' in window) return;
            toggleZoomAt(e.clientX, e.clientY);
        });

        var stage = o.querySelector('.klb-stage');
        stage.addEventListener('touchstart', onTouchStart, { passive: true });
        stage.addEventListener('touchmove', onTouchMove, { passive: true });
        stage.addEventListener('touchend', onTouchEnd);
        stage.addEventListener('touchcancel', function () {
            STATE.touch = null;
            STATE.pinch = null;
            var tr = o.querySelector('.klb-track');
            tr.classList.remove('is-dragging');
            tr.style.transform = '';
        });

        // Click on backdrop (the stage, outside the image) closes
        stage.addEventListener('click', function (e) {
            if ('ontouchstart' in window) return; // mobile handled by touchend
            if (e.target === stage) close();
        });

        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
    }

    function unwireEvents() {
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
    }

    var _resizeTimer = null;
    function onResize() {
        if (_resizeTimer) clearTimeout(_resizeTimer);
        _resizeTimer = setTimeout(fitImageAndStage, 60);
    }

    /* ---------- open / close ---------- */
    function open(globalIdx) {
        STATE.images = collectImages();
        if (!STATE.images.length) return;

        STATE.index = indexOfGlobal(typeof globalIdx === 'number' ? globalIdx : currentGlobalIdx());
        STATE.overlay = buildOverlay();
        document.body.appendChild(STATE.overlay);
        STATE.bodyOverflow = document.body.style.overflow;
        document.body.classList.add('klb-locked');

        renderSlide();
        wireEvents();

        // Trigger transition on next frame
        requestAnimationFrame(function () {
            STATE.overlay.classList.add('is-open');
        });

        var closeBtn = STATE.overlay.querySelector('.klb-close');
        if (closeBtn && closeBtn.focus) { try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); } }
    }

    function close() {
        if (!STATE.overlay) return;
        var o = STATE.overlay;
        o.classList.remove('is-open');
        unwireEvents();
        document.body.classList.remove('klb-locked');
        document.body.style.overflow = STATE.bodyOverflow;
        setTimeout(function () {
            if (o && o.parentNode) o.parentNode.removeChild(o);
        }, 220);
        STATE.overlay = null;
        STATE.touch = null;
        STATE.pinch = null;
        STATE.scale = 1;
        STATE.tx = 0;
        STATE.ty = 0;
    }

    /* ---------- mobile "Tap to zoom" hint ---------- */
    function maybeShowMobileHint(container) {
        try {
            if (window.matchMedia && !window.matchMedia('(hover: none)').matches) return;
            if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) return;
        } catch (e) { /* no-op */ }

        var mode = (container.getAttribute('data-zoom-hint') || 'persistent').toLowerCase();
        if (mode === 'off') return;

        if (mode === 'once') {
            try {
                if (window.localStorage && window.localStorage.getItem('kinsora_pswp_hint_seen') === '1') return;
            } catch (e) { /* no-op */ }
        }

        var label = container.getAttribute('data-zoom-hint-label') || 'Tap to zoom';
        var side = (container.getAttribute('data-zoom-hint-side') || 'right').toLowerCase();
        var hint = document.createElement('div');
        hint.className = 'kinsora-zoom-hint'
            + (mode === 'persistent' ? ' kinsora-zoom-hint--persistent' : '')
            + (side === 'left' ? ' kinsora-zoom-hint--left' : '');
        hint.setAttribute('aria-hidden', 'true');
        hint.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>' +
            '<span></span>';
        hint.querySelector('span').textContent = label;
        container.appendChild(hint);

        setTimeout(function () { hint.classList.add('is-visible'); }, 400);

        if (mode === 'once') {
            setTimeout(function () {
                hint.classList.remove('is-visible');
                setTimeout(function () { if (hint.parentNode) hint.parentNode.removeChild(hint); }, 400);
            }, 3800);
            try { if (window.localStorage) window.localStorage.setItem('kinsora_pswp_hint_seen', '1'); } catch (e) { /* no-op */ }
        }
    }

    /* ---------- init ---------- */
    function init() {
        var container = document.getElementById('mainMediaContainer');
        if (!container) return;
        if (!document.querySelector('.kinsora-pswp-gallery a')) return;

        injectStyles();
        container.classList.add('kinsora-zoomable');

        container.addEventListener('click', function (e) {
            var t = e.target;
            if (t && t.closest && (t.closest('video') || t.closest('iframe') || t.closest('model-viewer'))) return;
            if (t && (t.tagName === 'VIDEO' || t.tagName === 'IFRAME' || t.tagName === 'MODEL-VIEWER')) return;
            open(currentGlobalIdx());
        });

        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '0');
        container.setAttribute('role', 'button');
        container.setAttribute('aria-label', 'Open image in full screen to zoom');
        container.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                open(currentGlobalIdx());
            }
        });

        maybeShowMobileHint(container);

        // Expose a tiny API (useful if any external code wants to open programmatically)
        window.KinsoraLightbox = { open: open, close: close };

        if (window.console && console.log) {
            console.log('[Kinsora Gallery] custom lightbox ready — anchors:', document.querySelectorAll('.kinsora-pswp-gallery a').length);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
